import React, { useState, useEffect, useRef, useCallback } from 'react';
import { DataService } from '../../services/dataService';
import { isLocalMode } from '../../utils/apiTarget';
import { useTheme } from '../../utils/ThemeSwitcher';
import './DataLoader.css'


const LoadPrimaryData = ({ onDataLoaded, isDataPreviewVisible, onClearPreviousState, projectId, onLoadingStart, onLoadingEnd }) => {

  const [isExpandedPS, setIsExpandedPS] = useState(true); // Start expanded by default
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileType, setFileType] = useState('excel');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const { isDarkMode } = useTheme();

  const colors = {
    // Backgrounds
    surfaceBg: isDarkMode ? '#18181b' : '#f4f4f5',
    inputBg: isDarkMode ? '#18181b' : '#ffffff',
    
    // Borders  
    border: isDarkMode ? '#27272a' : '#e4e4e7',
    borderHover: isDarkMode ? '#3f3f46' : '#d4d4d8',
    
    // Text
    text: isDarkMode ? '#fafafa' : '#18181b',
    textSecondary: isDarkMode ? '#a1a1aa' : '#71717a',
    textMuted: isDarkMode ? '#71717a' : '#a1a1aa',
    
    // Accents
    primary: '#2563EB',
    primaryHover: '#1d4ed8',
    success: '#2563EB',
  }; 

  // ── Project media folder (project-wide; consumed by Data Preview, Audio Audit, etc.) ──
  // Storage: backend `media_settings` (with audio_settings fallback). Folder
  // picker only works in Tauri (DataQuad Desktop); web users see no UI here.
  const [mediaDir, setMediaDir] = useState(null);
  const [tauriApi, setTauriApi] = useState(null);
  const [pickingFolder, setPickingFolder] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        if (!window.__TAURI_INTERNALS__) return;
        const { invoke } = await import('@tauri-apps/api/core');
        if (mounted) setTauriApi({ invoke });
      } catch (e) {
        // Not in Tauri or import failed — silently skip; web mode has no picker.
      }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      try {
        const settings = await DataService.getMediaSettings(projectId);
        if (!cancelled) setMediaDir(settings?.media_dir || null);
      } catch (e) {
        if (!cancelled) setMediaDir(null);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  const handlePickMediaFolder = useCallback(async () => {
    if (!tauriApi?.invoke || !projectId) return;
    setPickingFolder(true);
    try {
      const path = await tauriApi.invoke('pick_folder', {
        title: 'Choose your project media folder',
      });
      if (!path) {
        setPickingFolder(false);
        return;
      }
      setMediaDir(path);
      try {
        await DataService.saveMediaSettings(projectId, { media_dir: path });
      } catch (e) {
        console.warn('Could not persist media settings:', e);
      }
    } catch (e) {
      console.error('Folder picker failed:', e);
    } finally {
      setPickingFolder(false);
    }
  }, [tauriApi, projectId]);

  const handleClearMediaFolder = useCallback(async () => {
    if (!projectId) return;
    setMediaDir(null);
    try {
      await DataService.saveMediaSettings(projectId, { media_dir: '' });
    } catch (e) {
      console.warn('Could not clear media settings:', e);
    }
  }, [projectId]);

  // Main data source categories
  const [dataSource, setDataSource] = useState('file');
  const [apiConnection, setApiConnection] = useState('surveycto');
  const [databaseConnection, setDatabaseConnection] = useState('postgresql');
  
  // File upload state
  const [availableSqliteTables, setAvailableSqliteTables] = useState([]);
  const [selectedSqliteTable, setSelectedSqliteTable] = useState('');
  const [showSqliteSelector, setShowSqliteSelector] = useState(false);

  // Database connection states
  const [postgresConfig, setPostgresConfig] = useState({
    host: '',
    port: '5432',
    database: '',
    user: '',
    password: '',
    table_name: ''
  });
  
  const [mysqlConfig, setMysqlConfig] = useState({
    host: '',
    port: '3306',
    database: '',
    user: '',
    password: '',
    table_name: ''
  });
  
  const [mongoConfig, setMongoConfig] = useState({
    host: '',
    port: '27017',
    database: '',
    collection: '',
    username: '',
    password: ''
  });
  
  const [mariadbConfig, setMariadbConfig] = useState({
    host: '',
    port: '3306',
    database: '',
    user: '',
    password: '',
    table_name: ''
  });
  
  const [dynamoConfig, setDynamoConfig] = useState({
    region: 'us-east-1',
    access_key_id: '',
    secret_access_key: '',
    table_name: ''
  });

  const [availableTables, setAvailableTables] = useState([]);
  const [fetchingTables, setFetchingTables] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('');

  // API connection states
  const [surveyCTOConfig, setSurveyCTOConfig] = useState({
    server_url: '',
    username: '',
    password: '',
    form_id: ''
  });
  
  const [koboConfig, setKoboConfig] = useState({
    server_url: 'https://kf.kobotoolbox.org',
    username: '',
    password: '',
    asset_uid: '',
    export_setting_id: '',
    sheet_name: ''
  });
  
  const [odkConfig, setOdkConfig] = useState({
    server_url: '',
    email: '',
    password: '',
    odk_project_id: '',
    form_id: ''
  });
  
  const [surveySolutionsConfig, setSurveySolutionsConfig] = useState({
    server_url: '',
    username: '',
    password: '',
    questionnaire_id: '',
    workspace: ''
  });

  const [availableForms, setAvailableForms] = useState([]);
  const [fetchingForms, setFetchingForms] = useState(false);
  const [apiConnectionStatus, setApiConnectionStatus] = useState('');

  // API Config Persistence State
  const [savedAPIConfig, setSavedAPIConfig] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [refreshInterval, setRefreshInterval] = useState('manual');
  const [isUpdatingSchedule, setIsUpdatingSchedule] = useState(false);

  // Icon components (keeping existing ones)
  const FileUploadIcon = ({ size = 24, color = "currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2Z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M14 2V8H20" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M16 13L12 17L8 13" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M12 17V9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const APIIcon = ({ size = 24, color = "currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const DatabaseIcon = ({ size = 24, color = "currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <ellipse cx="12" cy="5" rx="9" ry="3" stroke={color} strokeWidth="2"/>
      <path d="M21 12C21 13.66 16.97 15 12 15S3 13.66 3 12" stroke={color} strokeWidth="2"/>
      <path d="M3 5V19C3 20.66 7.03 22 12 22S21 20.66 21 19V5" stroke={color} strokeWidth="2"/>
    </svg>
  );

  // Database specific icons
  const PostgreSQLIcon = ({ size = 24, color = isDarkMode ? "#f0f0f0ff" : "#4e4e4eff"}) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} xmlns="http://www.w3.org/2000/svg">
      <path d="M17.128 0C16.364 0 15.718.162 15.217.438C15.043.547 14.925.688 14.925.859C14.925 1.03 15.043 1.171 15.217 1.28C15.718 1.556 16.364 1.718 17.128 1.718C17.892 1.718 18.538 1.556 19.04 1.28C19.213 1.171 19.331 1.03 19.331 .859C19.331.688 19.213.547 19.04.438C18.538.162 17.892 0 17.128 0ZM6.872 2.156C5.936 2.156 5.09 2.42 4.466 2.883C4.152 3.116 3.9 3.407 3.732 3.735C3.563 4.063 3.482 4.422 3.5 4.781V4.844C3.5 5.015 3.618 5.156 3.792 5.265C4.294 5.541 4.94 5.703 5.704 5.703H6.059C6.059 5.703 6.086 5.703 6.122 5.703C6.886 5.703 7.532 5.541 8.034 5.265C8.207 5.156 8.325 5.015 8.325 4.844V4.781C8.343 4.422 8.262 4.063 8.094 3.735C7.926 3.407 7.674 3.116 7.36 2.883C6.736 2.42 5.89 2.156 5.954 2.156H6.872ZM17.128 5.844C16.364 5.844 15.718 6.006 15.217 6.282C15.043 6.391 14.925 6.532 14.925 6.703C14.925 6.874 15.043 7.015 15.217 7.124C15.718 7.4 16.364 7.562 17.128 7.562C17.892 7.562 18.538 7.4 19.04 7.124C19.213 7.015 19.331 6.874 19.331 6.703C19.331 6.532 19.213 6.391 19.04 6.282C18.538 6.006 17.892 5.844 17.128 5.844Z"/>
    </svg>
  );

  const MySQLIcon = ({ size = 20, color = isDarkMode ? "#f0f0f0ff" : "#4e4e4eff" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} xmlns="http://www.w3.org/2000/svg">
      <path d="M16.405 5.501c-.115 0-.193.014-.274.033v.013h.014c.054.104.146.18.214.273.054.107.1.214.154.32l.014-.015c.094-.066.14-.172.14-.333-.04-.047-.046-.094-.08-.14-.04-.067-.126-.1-.18-.153zM5.77 18.695h-.927a50.854 50.854 0 00-.27-4.41h-.008l-1.41 4.41H2.45l-1.4-4.41h-.01a72.892 72.892 0 00-.195 4.41H.002c.055-1.966.192-3.81.41-5.53h1.15c.316 1.043.664 2.18 1.044 3.41h.008c.484-1.324.945-2.46 1.38-3.41h1.095c.242 1.64.378 3.484.433 5.53zM14.214 18.695v-5.446h.904v4.85h2.375v.596h-3.279zm-7.312-5.446c.5-.054 1.27-.081 2.064-.081 1.03 0 1.758.19 2.227.567.394.313.615.744.615 1.405 0 .827-.25 1.39-.615 1.743-.456.434-1.17.65-2.227.65-.243 0-.486-.014-.729-.027v2.133H7.333v-6.39h-.431zm.904 3.563c.216.014.459.027.729.027.946 0 1.484-.406 1.484-1.108 0-.675-.513-1.001-1.403-1.001-.324 0-.567.027-.81.054v2.028zm21.312 1.883h-.902v-2.375c0-.787-.297-1.176-.891-1.176-.446 0-.783.298-.899.679-.027.1-.041.223-.041.353v2.52h-.902v-2.453c0-.742-.283-1.098-.875-1.098-.431 0-.783.27-.914.68-.041.135-.055.27-.055.407v2.464h-.902v-3.984h.902v.596h.014c.189-.338.54-.679 1.066-.679.432 0 .797.19 1.014.542h.027c.216-.297.608-.542 1.122-.542.405 0 .797.135 1.095.461.351.378.446.865.446 1.568v2.038h-.405z"/>
    </svg>
  );

  const MongoDBIcon = ({ size = 20, color = isDarkMode ? "#f0f0f0ff" : "#4e4e4eff" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} xmlns="http://www.w3.org/2000/svg">
      <path d="M17.193 9.555c-1.264-5.58-4.252-7.414-4.573-8.115-.28-.394-.53-.954-.735-1.44-.036.495-.055.685-.523 1.184-.723.566-4.438 3.682-4.74 10.02-.282 5.912 4.27 9.435 4.888 9.884l.07.05A73.49 73.49 0 0111.91 24h.481c.114-3.032.284-6.014.402-9.684.023-.716.05-1.432.05-2.146.001-.257.007-.514.007-.771C12.84 10.213 13.97 8.818 17.193 9.555z"/>
    </svg>
  );

  const DynamoDBIcon = ({ size = 20, color = isDarkMode ? "#f0f0f0ff" : "#4e4e4eff" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} xmlns="http://www.w3.org/2000/svg">
      <path d="M16.606 21.994h-.65c-.389 0-.717-.283-.76-.656l-.324-2.825c-1.78.073-3.756-.146-5.732-.657l-1.201 2.53c-.182.383-.606.55-.982.384l-.606-.267c-.377-.166-.55-.606-.384-.982l1.18-2.486C5.487 16.04 4.066 14.814 3 13.326l-2.53 1.201c-.383.182-.816.01-.982-.384l-.267-.606c-.166-.377.01-.816.384-.982l2.486-1.18C1.096 9.715.146 7.739.073 5.959L-.98 5.635C-1.353 5.592-1.636 5.264-1.636 4.875v-.65c0-.389.283-.717.656-.76l2.825-.324c.073-1.78.366-3.756.877-5.732L-.162 1.61c-.383-.182-.55-.606-.384-.982l.267-.606c.166-.377.606-.55.982-.384l2.486 1.18C4.184.823 5.41-.598 6.898-1.664l-1.201-2.53c-.182-.383-.01-.816.384-.982l.606-.267c.377-.166.816.01.982.384l1.18 2.486c1.659-.995 3.635-1.945 5.415-2.018l.324-2.825c.043-.373.371-.656.76-.656h.65c.389 0 .717.283.76.656l.324 2.825c1.78-.073 3.756.146 5.732.657l1.201-2.53c.182-.383.606-.55.982-.384l.606.267c.377.166.55.606.384.982l-1.18 2.486c1.659.995 3.081 2.221 4.147 3.709l2.53-1.201c.383-.182.816-.01.982.384l.267.606c.166.377-.01.816-.384.982l-2.486 1.18c.995 1.659 1.945 3.635 2.018 5.415l2.825.324c.373.043.656.371.656.76v.65c0 .389-.283.717-.656.76l-2.825.324c-.073 1.78-.366 3.756-.877 5.732l2.486 1.18c.383.182.55.606.384.982l-.267.606c-.166.377-.606.55-.982.384l-2.53-1.201c-.995 1.659-2.221 3.081-3.709 4.147l1.201 2.53c.182.383.01.816-.384.982l-.606.267c-.377.166-.816-.01-.982-.384l-1.18-2.486c-1.659.995-3.635 1.945-5.415 2.018l-.324 2.825c-.043.373-.371.656-.76.656zM12 18c3.314 0 6-2.686 6-6s-2.686-6-6-6-6 2.686-6 6 2.686 6 6 6z"/>
    </svg>
  );

  const MariaDBIcon = ({ size = 20, color = isDarkMode ? "#f0f0f0ff" : "#4e4e4eff" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} xmlns="http://www.w3.org/2000/svg">
      <path d="M12.003 2.001c-5.522 0-10 4.477-10 9.999s4.478 10.001 10 10.001c5.523 0 10.001-4.479 10.001-10.001 0-5.522-4.478-9.999-10.001-9.999zm5.527 14.427c-.344.688-1.031 1.031-2.063 1.031-.344 0-.687-.063-.999-.188-.313-.125-.594-.312-.844-.562-.25-.25-.437-.531-.562-.844-.125-.312-.188-.656-.188-.999 0-.687.219-1.281.656-1.781.437-.5 1.031-.75 1.781-.75.343 0 .656.063.968.188.313.125.594.312.844.562.25.25.437.531.562.844.125.312.188.656.188.999 0 .687-.219 1.281-.656 1.781z"/>
    </svg>
  );

  // API specific icons
  // API specific icons
  const SurveyCTOIcon = ({ size = 20, color = isDarkMode ? "#f0f0f0ff" : "#4e4e4eff" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2L3 7V17L12 22L21 17V7L12 2Z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M12 22V12" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M12 12L3 7" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M12 12L21 7" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="12" cy="12" r="2" fill={color}/>
    </svg>
  );

  const KoBoIcon = ({ size = 20, color = isDarkMode ? "#f0f0f0ff" : "#4e4e4eff" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="3" y="3" width="18" height="18" rx="2" stroke={color} strokeWidth="2"/>
      <path d="M8 12L11 15L16 9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="12" cy="7" r="1.5" fill={color}/>
    </svg>
  );

  const ODKIcon = ({ size = 20, color = isDarkMode ? "#f0f0f0ff" : "#4e4e4eff" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2Z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M14 2V8H20" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M9 13H15" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M9 17H15" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const SurveySolutionsIcon = ({ size = 20, color = isDarkMode ? "#f0f0f0ff" : "#4e4e4eff" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M21 16V8C21 6.9 20.1 6 19 6H5C3.9 6 3 6.9 3 8V16C3 17.1 3.9 18 5 18H19C20.1 18 21 17.1 21 16Z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M7 10H17" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M7 14H13" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  // Handler functions (keeping all existing handlers)
  const toggleExpandPS = () => {
    setIsExpandedPS(!isExpandedPS);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setError(null);
      setAvailableSqliteTables([]);
      setSelectedSqliteTable('');
      setShowSqliteSelector(false);
    }
  };

  // Database configuration handlers (keeping existing)
  const handlePostgresConfigChange = (e) => {
    const { name, value } = e.target;
    setPostgresConfig(prev => ({
      ...prev,
      [name]: value
    }));
    if (['host', 'port', 'database', 'user', 'password'].includes(name)) {
      setConnectionStatus('');
      setAvailableTables([]);
    }
  };

  const handleMysqlConfigChange = (e) => {
    const { name, value } = e.target;
    setMysqlConfig(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleMongoConfigChange = (e) => {
    const { name, value } = e.target;
    setMongoConfig(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleMariadbConfigChange = (e) => {
    const { name, value } = e.target;
    setMariadbConfig(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (['host', 'port', 'database', 'user', 'password'].includes(name)) {
      setConnectionStatus('');
      setAvailableTables([]);
    }
  };

  const handleDynamoConfigChange = (e) => {
    const { name, value } = e.target;
    setDynamoConfig(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // API configuration handlers (keeping existing)
  const handleSurveyCTOConfigChange = (e) => {
    const { name, value } = e.target;
    setSurveyCTOConfig(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (['server_url', 'username', 'password'].includes(name)) {
      setApiConnectionStatus('');
      setAvailableForms([]);
      setError(null);
    }
  };

  const fetchSurveyCTOForms = async () => {
    try {
      setFetchingForms(true);
      setError(null);
      setApiConnectionStatus('connecting');
      
      if (!surveyCTOConfig.server_url || !surveyCTOConfig.username || !surveyCTOConfig.password) {
        throw new Error('Server URL, username, and password are required to fetch forms');
      }
      
      const forms = await DataService.getSurveyCTOForms(surveyCTOConfig);
      setAvailableForms(forms);
      setApiConnectionStatus('connected');
      
      if (forms.length === 0) {
        setError('No forms found. Please check your permissions.');
      }
      
    } catch (error) {
      setError(error.message || 'Failed to fetch forms');
      setApiConnectionStatus('failed');
      setAvailableForms([]);
    } finally {
      setFetchingForms(false);
    }
  };

  const validateSurveyCTOConfig = () => {
    const { server_url, username, password, form_id } = surveyCTOConfig;
    
    if (!server_url.trim()) {
      throw new Error('Server URL is required');
    }
    
    if (!username.trim()) {
      throw new Error('Username is required');
    }
    
    if (!password.trim()) {
      throw new Error('Password is required');
    }
    
    if (!form_id.trim()) {
      throw new Error('Form ID is required');
    }
    
    try {
      new URL(server_url);
    } catch {
      throw new Error('Please enter a valid Server URL (e.g., https://yourserver.surveycto.com)');
    }
    
    return true;
  };

  const handleKoboConfigChange = (e) => {
    const { name, value } = e.target;
    setKoboConfig(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Helper function to format time since last refresh
  const formatTimeSince = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)} days ago`;
    return date.toLocaleDateString();
  };

  const handleOdkConfigChange = (e) => {
    const { name, value } = e.target;
    setOdkConfig(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSurveySolutionsConfigChange = (e) => {
    const { name, value } = e.target;
    setSurveySolutionsConfig(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const fetchTables = async () => {
    try {
      setFetchingTables(true);
      setError(null);
      setConnectionStatus('connecting');
      
      let config;
      let tables;
      
      switch (databaseConnection) {
        case 'postgresql':
          config = postgresConfig;
          tables = await DataService.getPostgresTables(config);
          break;
        case 'mysql':
          config = mysqlConfig;
          tables = await DataService.getMysqlTables(config);
          break;
        case 'mariadb':
          config = mariadbConfig;
          tables = await DataService.getMysqlTables(config);
          break;
        default:
          throw new Error(`Table fetching for ${databaseConnection} is not yet implemented`);
      }
      
      setAvailableTables(tables);
      setConnectionStatus('connected');
    } catch (error) {
      setError(error.message || 'Failed to fetch tables');
      setConnectionStatus('failed');
      setAvailableTables([]);
    } finally {
      setFetchingTables(false);
    }
  };

  const handleSqliteTableChange = async (tableName) => {
    
    setSelectedSqliteTable(tableName);
    setLoading(true);
    setError(null);
    if (onLoadingStart) onLoadingStart();
    
    try {
      if (onClearPreviousState) {
        onClearPreviousState();
      }
      
      const loadedData = await DataService.loadSqliteTable(selectedFile, tableName);
      if (onLoadingEnd) onLoadingEnd();
      onDataLoaded(loadedData);
    } catch (error) {
      console.error('Error loading SQLite table:', error);
      setError(error.message || 'Failed to load SQLite table');
    } finally {
      setLoading(false);
    }
  };

  const isLoadDataDisabled = () => {
    if (loading) return true;
    
    if (dataSource === 'api' && apiConnection === 'surveysolutions') {
      return true;
    }
    
    return false;
  };

  const handleLoadData = async () => {
    if (!dataSource) {
      setError('Please select a data source');
      return;
    }

    if (dataSource === 'api' && apiConnection === 'surveysolutions') {
      setError('Survey Solutions uses an async export API. For real-time access, use PostgreSQL under Database Connection.');
      return;
    }

    setError(null);
    setLoading(true);
    
    // CRITICAL: Notify parent to show fullscreen loading overlay
    if (onLoadingStart) onLoadingStart();
    
    try {
      if (onClearPreviousState) {
        onClearPreviousState();
      }
      
      let loadedData;
      const initialPage = 1;
      const initialPageSize = 100;

      switch (dataSource) {
        case 'file':
          if (!selectedFile) {
            throw new Error('Please select a file');
          }
          loadedData = await DataService.loadData(selectedFile, fileType, initialPage, initialPageSize, projectId);
          
          if (fileType === 'sqlite' && loadedData.available_tables) {
            setAvailableSqliteTables(loadedData.available_tables);
            setSelectedSqliteTable(loadedData.selected_table);
            setShowSqliteSelector(true);
          }
          break;
          
        case 'database':
          if (databaseConnection === 'postgresql' && !postgresConfig.table_name) {
            throw new Error('Please select a table');
          }
          if (databaseConnection === 'mysql' && !mysqlConfig.table_name) {
            throw new Error('Please select a table');
          }
          if (databaseConnection === 'mariadb' && !mariadbConfig.table_name) {
            throw new Error('Please select a table');
          }
          if (databaseConnection === 'mongodb' && !mongoConfig.collection) {
            throw new Error('Please select a collection');
          }
          if (databaseConnection === 'dynamodb' && !dynamoConfig.table_name) {
            throw new Error('Please select a table');
          }
          
          let dbConfig;
          switch (databaseConnection) {
            case 'postgresql':
              dbConfig = postgresConfig;
              loadedData = await DataService.loadPostgresData(dbConfig);
              break;
            case 'mysql':
              dbConfig = mysqlConfig;
              loadedData = await DataService.loadMysqlData(dbConfig);
              break;
            case 'mariadb':
              dbConfig = mariadbConfig;
              loadedData = await DataService.loadMariadbData(dbConfig);
              break;
            case 'mongodb':
              dbConfig = mongoConfig;
              loadedData = await DataService.loadMongoData(dbConfig);
              break;
            case 'dynamodb':
              dbConfig = dynamoConfig;
              loadedData = await DataService.loadDynamoData(dbConfig);
              break;
            default:
              throw new Error(`Database connection type '${databaseConnection}' is not yet implemented`);
          }
          break;
          
        case 'api':
          switch (apiConnection) {
            case 'surveycto':
              validateSurveyCTOConfig();
              loadedData = await DataService.loadSurveyCTOData(surveyCTOConfig, projectId, 'primary');
              break;
            case 'kobo':
              loadedData = await DataService.loadKoboData(koboConfig, projectId, 'primary');
              break;
            case 'odk':
              loadedData = await DataService.loadOdkData(odkConfig, projectId, 'primary');
              break;
            case 'surveysolutions':
              throw new Error('Survey Solutions uses an async export API. For real-time access, use PostgreSQL under Database Connection.');
            default:
              throw new Error(`API connection type '${apiConnection}' is not yet implemented`);
          }
          break;
          
        default:
          throw new Error('Invalid data source selected');
      }

      console.log('Data loaded successfully:', loadedData);
      
      if (!loadedData) {
        throw new Error('No data received from the selected source');
      }
      
      // Save API config after successful load (for refresh functionality)
      if (dataSource === 'api' && projectId) {
        try {
          let configToSave = null;
          
          switch (apiConnection) {
            case 'kobo':
              configToSave = {
                connection_type: 'kobo',
                settings: { ...koboConfig },
                auto_refresh: true
              };
              break;
            case 'surveycto':
              configToSave = {
                connection_type: 'surveycto',
                settings: { ...surveyCTOConfig },
                auto_refresh: true
              };
              break;
            case 'odk':
              configToSave = {
                connection_type: 'odk',
                settings: { ...odkConfig },
                auto_refresh: true
              };
              break;
          }
          
          if (configToSave) {
            await DataService.saveAPIConfig(projectId, 'primary', configToSave);
            setSavedAPIConfig(configToSave);
            setLastRefreshed(new Date().toISOString());
            console.log('API config saved for future refresh');
          }
        } catch (saveError) {
          console.warn('Failed to save API config (non-critical):', saveError);
        }
      }
      
      // Save database config after successful load (for refresh functionality)
      if (dataSource === 'database' && projectId) {
        try {
          let configToSave = null;
          
          switch (databaseConnection) {
            case 'postgresql':
              configToSave = {
                connection_type: 'postgresql',
                settings: { ...postgresConfig },
                auto_refresh: true
              };
              break;
            case 'mysql':
              configToSave = {
                connection_type: 'mysql',
                settings: { ...mysqlConfig },
                auto_refresh: true
              };
              break;
            case 'mariadb':
              configToSave = {
                connection_type: 'mariadb',
                settings: { ...mariadbConfig },
                auto_refresh: true
              };
              break;
            case 'mongodb':
              configToSave = {
                connection_type: 'mongodb',
                settings: { ...mongoConfig },
                auto_refresh: true
              };
              break;
            case 'dynamodb':
              configToSave = {
                connection_type: 'dynamodb',
                settings: { ...dynamoConfig },
                auto_refresh: true
              };
              break;
          }
          
          if (configToSave) {
            await DataService.saveAPIConfig(projectId, 'primary', configToSave);
            setSavedAPIConfig(configToSave);
            setLastRefreshed(new Date().toISOString());
            console.log('Database config saved for future refresh');
          }
        } catch (saveError) {
          console.warn('Failed to save database config (non-critical):', saveError);
        }
      }
      
      if (onLoadingEnd) onLoadingEnd();
      onDataLoaded(loadedData);
    } catch (error) {
      console.error('Error in handleLoadData:', error);
      setError(error.message || 'An error occurred while loading the data');
      if (onLoadingEnd) onLoadingEnd();
    } finally {
      setLoading(false);
    }
  };
  

  useEffect(() => {
    if (isDataPreviewVisible) {
      setIsExpandedPS(true);
    }
  }, [isDataPreviewVisible]);

  // Load saved API config when component mounts or projectId changes
  useEffect(() => {
    const loadSavedAPIConfig = async () => {
      if (!projectId) return;
      
      try {
        const result = await DataService.getAPIConfig(projectId, 'primary');
        
        if (result.has_config) {
          console.log('Loaded saved API config:', result.config);
          setSavedAPIConfig(result.config);
          setLastRefreshed(result.config.last_loaded);
          setRefreshInterval(result.config.refresh_interval || 'manual');
          
          // Auto-populate form fields based on connection type
          const { connection_type, settings } = result.config;
          
          if (connection_type === 'kobo') {
            setDataSource('api');
            setApiConnection('kobo');
            setKoboConfig(prev => ({
              ...prev,
              server_url: settings.server_url || prev.server_url,
              username: settings.username || '',
              asset_uid: settings.asset_uid || '',
              export_setting_id: settings.export_setting_id || '',
              sheet_name: settings.sheet_name || ''
              // Note: password not pre-filled for security - user must re-enter for new loads
            }));
          } else if (connection_type === 'surveycto') {
            setDataSource('api');
            setApiConnection('surveycto');
            setSurveyCTOConfig(prev => ({
              ...prev,
              server_url: settings.server_url || '',
              username: settings.username || '',
              form_id: settings.form_id || ''
            }));
          } else if (connection_type === 'odk') {
            setDataSource('api');
            setApiConnection('odk');
            setOdkConfig(prev => ({
              ...prev,
              server_url: settings.server_url || '',
              email: settings.email || '',
              odk_project_id: settings.odk_project_id || '',
              form_id: settings.form_id || ''
            }));
          } else if (connection_type === 'postgresql') {
            setDataSource('database');
            setDatabaseConnection('postgresql');
            setPostgresConfig(prev => ({
              ...prev,
              host: settings.host || '',
              port: settings.port || '5432',
              database: settings.database || '',
              user: settings.user || '',
              table_name: settings.table_name || ''
            }));
          } else if (connection_type === 'mysql') {
            setDataSource('database');
            setDatabaseConnection('mysql');
            setMysqlConfig(prev => ({
              ...prev,
              host: settings.host || '',
              port: settings.port || '3306',
              database: settings.database || '',
              user: settings.user || '',
              table_name: settings.table_name || ''
            }));
          } else if (connection_type === 'mariadb') {
            setDataSource('database');
            setDatabaseConnection('mariadb');
            setMariadbConfig(prev => ({
              ...prev,
              host: settings.host || '',
              port: settings.port || '3306',
              database: settings.database || '',
              user: settings.user || '',
              table_name: settings.table_name || ''
            }));
          } else if (connection_type === 'mongodb') {
            setDataSource('database');
            setDatabaseConnection('mongodb');
            setMongoConfig(prev => ({
              ...prev,
              host: settings.host || '',
              port: settings.port || '27017',
              database: settings.database || '',
              username: settings.username || '',
              collection: settings.collection || ''
            }));
          } else if (connection_type === 'dynamodb') {
            setDataSource('database');
            setDatabaseConnection('dynamodb');
            setDynamoConfig(prev => ({
              ...prev,
              region: settings.region || 'us-east-1',
              access_key_id: settings.access_key_id || '',
              table_name: settings.table_name || ''
            }));
          }
        }
      } catch (error) {
        console.log('No saved API config found or error loading:', error.message);
      }
    };
    
    loadSavedAPIConfig();
  }, [projectId]);

  // Handle refresh data using saved credentials
  const handleRefreshData = async () => {
    if (!savedAPIConfig || !projectId) {
      setError('No saved configuration to refresh');
      return;
    }
    
    setIsRefreshing(true);
    setError(null);
    
    if (onLoadingStart) onLoadingStart();
    
    try {
      const result = await DataService.refreshAPIData(projectId, 'primary');
      console.log('Data refreshed successfully:', result);
      
      setLastRefreshed(result.refreshed_at || new Date().toISOString());
      
      if (onLoadingEnd) onLoadingEnd();
      onDataLoaded(result);
    } catch (error) {
      console.error('Error refreshing data:', error);
      setError('Failed to refresh data: ' + error.message);
      if (onLoadingEnd) onLoadingEnd();
    } finally {
      setIsRefreshing(false);
    }
  };

  // Handle disconnect - remove saved config
  const handleDisconnect = async () => {
    if (!savedAPIConfig || !projectId) return;
    
    const connectionName = getConnectionDisplayName(savedAPIConfig.connection_type);
    
    if (!window.confirm(`Disconnect from ${connectionName}? Your saved credentials will be removed but loaded data will remain.`)) {
      return;
    }
    
    try {
      await DataService.disconnectDataSource(projectId, 'primary');
      setSavedAPIConfig(null);
      setLastRefreshed(null);
      setRefreshInterval('manual');
      console.log('Disconnected from data source');
    } catch (error) {
      console.error('Error disconnecting:', error);
      setError('Failed to disconnect: ' + error.message);
    }
  };

  // Helper to get display name for connection type
  const getConnectionDisplayName = (connectionType) => {
    const names = {
      'kobo': 'KoBoToolbox',
      'surveycto': 'SurveyCTO',
      'odk': 'ODK Central',
      'postgresql': 'PostgreSQL',
      'mysql': 'MySQL',
      'mariadb': 'MariaDB',
      'mongodb': 'MongoDB',
      'dynamodb': 'DynamoDB'
    };
    return names[connectionType] || connectionType.charAt(0).toUpperCase() + connectionType.slice(1);
  };

  // Handle refresh schedule change
  const handleScheduleChange = async (newInterval) => {
    if (!projectId) return;
    
    setIsUpdatingSchedule(true);
    try {
      await DataService.updateRefreshSchedule(projectId, 'primary', newInterval);
      setRefreshInterval(newInterval);
      console.log('Refresh schedule updated to:', newInterval);
    } catch (error) {
      console.error('Error updating schedule:', error);
      setError('Failed to update refresh schedule: ' + error.message);
    } finally {
      setIsUpdatingSchedule(false);
    }
  };

  const renderFileUploadConfig = () => (
    <div style={{ 
      marginBottom: '20px', 
      display: 'flex', 
      flexDirection: 'column', 
      gap: '20px',
      padding: '0px',
      borderRadius: '10px',
      width: '100%',
    }}>
      <input 
        type="file" 
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileChange}
        accept=".xlsx,.xls,.csv,.dta,.db"
      />

      {/* File Format Selection - Tab Style */}
      <div className="dl-platform-tabs">
        {[
          { id: 'excel', label: 'Excel', ext: '.xlsx, .xls' },
          { id: 'csv', label: 'CSV', ext: '.csv' },
          { id: 'stata', label: 'Stata', ext: '.dta' },
          { id: 'sqlite', label: 'SQLite', ext: '.db' },
        ].map(({ id, label, ext }) => (
          <button
            key={id}
            className={`dl-platform-tab ${fileType === id ? 'active' : ''}`}
            onClick={() => setFileType(id)}
          >
            {label}
            <span style={{ fontSize: '10px', opacity: 0.6 }}>{ext}</span>
          </button>
        ))}
      </div>
      
      {/* File Selection */}
      <div className="dl-file-picker-row">
        <button
          onClick={() => fileInputRef.current.click()}
          className={`dl-file-btn ${selectedFile ? 'selected' : ''}`}
        >
          {selectedFile ? (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          ) : (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17 8 12 3 7 8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
          )}
          {selectedFile ? 'Change file' : 'Choose file'}
        </button>

        {selectedFile ? (
          <div style={{ flex: 1 }}>
            <div style={{ 
              fontSize: '13px',
              color: colors.text,
              fontWeight: '500',
              marginBottom: '2px',
            }}>
              {selectedFile.name}
            </div>
            <div style={{ fontSize: '12px', color: colors.textMuted }}>
              {(selectedFile.size / 1024).toFixed(1)} KB
            </div>
          </div>
        ) : (
          <span style={{ fontSize: '13px', color: colors.textMuted }}>
            No file selected
          </span>
        )}
      </div>
      
      {showSqliteSelector && availableSqliteTables.length > 0 && (
        <div style={{ marginTop: '10px' }}>
          <select
            value={selectedSqliteTable}
            onChange={(e) => handleSqliteTableChange(e.target.value)}
            style={{ 
              padding: '10px 16px',
              borderRadius: '10px',
              backgroundColor: isDarkMode ? '#313233ff' : '#FFFFFF',
              color: isDarkMode ? '#aaaaaaff' : '#353638ff',
              border: isDarkMode ? '1px solid #313233' : '1px solid #D1D5DB',
              marginRight: '10px',
              fontSize: '13px',
              fontWeight: '500',
              transition: 'all 0.3s ease'
            }}
          >
            {availableSqliteTables.map(table => (
              <option key={table} value={table}>{table}</option>
            ))}
          </select>
          <span style={{ 
            fontSize: '13px',
            color: isDarkMode ? '#9CA3AF' : '#6B7280'
          }}>
            Selected table: {selectedSqliteTable}
          </span>
        </div>
      )}
    </div>
  );

  const renderDatabaseConfig = () => {
    const inputStyle = {
      padding: '10px 14px',
      borderRadius: '10px',
      backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.7)',
      color: colors.text,
      border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.16)'}`,
      margin: '5px',
      width: '200px',
      fontSize: '13px',
      fontFamily: 'inherit',
      letterSpacing: '-0.005em',
      outline: 'none',
      transition: 'border-color 0.2s ease'
    };

    const selectStyle = {
      ...inputStyle,
      width: '180px',
      appearance: 'none',
      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23${isDarkMode ? 'a1a1aa' : '71717a'}' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'right 12px center',
      paddingRight: '40px',
      cursor: 'pointer',
    };

    const buttonStyle = {
      padding: '10px 20px',
      borderRadius: '100px',
      background: fetchingTables
        ? (isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)')
        : (isDarkMode ? '#f0ede4' : '#1a1a1a'),
      color: fetchingTables
        ? (isDarkMode ? '#6b6862' : '#908c82')
        : (isDarkMode ? '#0D0D0E' : '#F8F5EE'),
      border: 'none',
      cursor: fetchingTables ? 'not-allowed' : 'pointer',
      fontSize: '13px',
      fontWeight: '500',
      letterSpacing: '-0.005em',
      margin: '5px',
      fontFamily: 'inherit',
      transition: 'all 0.2s ease',
      opacity: fetchingTables ? 0.75 : 1,
    };

    const containerStyle = {
      padding: '20px',
      borderRadius: '10px',
      border: `0.5px solid ${colors.border}`
    };

    switch (databaseConnection) {
      case 'postgresql':
        return (
          <div style={containerStyle}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  placeholder="Host"
                  name="host"
                  value={postgresConfig.host}
                  onChange={handlePostgresConfigChange}
                  style={inputStyle}
                />
                <input
                  type="text"
                  placeholder="Port"
                  name="port"
                  value={postgresConfig.port}
                  onChange={handlePostgresConfigChange}
                  style={inputStyle}
                />
                <input
                  type="text"
                  placeholder="Database"
                  name="database"
                  value={postgresConfig.database}
                  onChange={handlePostgresConfigChange}
                  style={inputStyle}
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  placeholder="Username"
                  name="user"
                  value={postgresConfig.user}
                  onChange={handlePostgresConfigChange}
                  style={inputStyle}
                />
                <input
                  type="password"
                  placeholder="Password"
                  name="password"
                  value={postgresConfig.password}
                  onChange={handlePostgresConfigChange}
                  style={inputStyle}
                />
                <button
                  onClick={fetchTables}
                  disabled={fetchingTables}
                  style={buttonStyle}
                >
                  {fetchingTables ? 'Connecting...' : 'Get Tables'}
                </button>
              </div>
              {availableTables.length > 0 && (
                <select
                  name="table_name"
                  value={postgresConfig.table_name}
                  onChange={handlePostgresConfigChange}
                  style={selectStyle}
                >
                  <option value="">Select a table</option>
                  {availableTables.map(table => (
                    <option key={table} value={table}>{table}</option>
                  ))}
                </select>
              )}
            </div>
          </div>
        );
      
      case 'mysql':
        return (
          <div style={containerStyle}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  placeholder="Host"
                  name="host"
                  value={mysqlConfig.host}
                  onChange={handleMysqlConfigChange}
                  style={inputStyle}
                />
                <input
                  type="text"
                  placeholder="Port"
                  name="port"
                  value={mysqlConfig.port}
                  onChange={handleMysqlConfigChange}
                  style={inputStyle}
                />
                <input
                  type="text"
                  placeholder="Database"
                  name="database"
                  value={mysqlConfig.database}
                  onChange={handleMysqlConfigChange}
                  style={inputStyle}
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  placeholder="Username"
                  name="user"
                  value={mysqlConfig.user}
                  onChange={handleMysqlConfigChange}
                  style={inputStyle}
                />
                <input
                  type="password"
                  placeholder="Password"
                  name="password"
                  value={mysqlConfig.password}
                  onChange={handleMysqlConfigChange}
                  style={inputStyle}
                />
                <button
                  onClick={fetchTables}
                  disabled={fetchingTables}
                  style={buttonStyle}
                >
                  {fetchingTables ? 'Connecting...' : 'Get Tables'}
                </button>
              </div>
              {availableTables.length > 0 && (
                <select
                  name="table_name"
                  value={mysqlConfig.table_name}
                  onChange={handleMysqlConfigChange}
                  style={selectStyle}
                >
                  <option value="">Select a table</option>
                  {availableTables.map(table => (
                    <option key={table} value={table}>{table}</option>
                  ))}
                </select>
              )}
            </div>
          </div>
        );

      case 'mongodb':
        return (
          <div style={containerStyle}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  placeholder="Host"
                  name="host"
                  value={mongoConfig.host}
                  onChange={handleMongoConfigChange}
                  style={inputStyle}
                />
                <input
                  type="text"
                  placeholder="Port"
                  name="port"
                  value={mongoConfig.port}
                  onChange={handleMongoConfigChange}
                  style={inputStyle}
                />
                <input
                  type="text"
                  placeholder="Database"
                  name="database"
                  value={mongoConfig.database}
                  onChange={handleMongoConfigChange}
                  style={inputStyle}
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  placeholder="Collection"
                  name="collection"
                  value={mongoConfig.collection}
                  onChange={handleMongoConfigChange}
                  style={inputStyle}
                />
                <input
                  type="text"
                  placeholder="Username"
                  name="username"
                  value={mongoConfig.username}
                  onChange={handleMongoConfigChange}
                  style={inputStyle}
                />
                <input
                  type="password"
                  placeholder="Password"
                  name="password"
                  value={mongoConfig.password}
                  onChange={handleMongoConfigChange}
                  style={inputStyle}
                />
              </div>
            </div>
          </div>
        );

      case 'mariadb':
        return (
          <div style={containerStyle}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  placeholder="Host"
                  name="host"
                  value={mariadbConfig.host}
                  onChange={handleMariadbConfigChange}
                  style={inputStyle}
                />
                <input
                  type="text"
                  placeholder="Port"
                  name="port"
                  value={mariadbConfig.port}
                  onChange={handleMariadbConfigChange}
                  style={inputStyle}
                />
                <input
                  type="text"
                  placeholder="Database"
                  name="database"
                  value={mariadbConfig.database}
                  onChange={handleMariadbConfigChange}
                  style={inputStyle}
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  placeholder="Username"
                  name="user"
                  value={mariadbConfig.user}
                  onChange={handleMariadbConfigChange}
                  style={inputStyle}
                />
                <input
                  type="password"
                  placeholder="Password"
                  name="password"
                  value={mariadbConfig.password}
                  onChange={handleMariadbConfigChange}
                  style={inputStyle}
                />
                <button
                  onClick={fetchTables}
                  disabled={fetchingTables}
                  style={buttonStyle}
                >
                  {fetchingTables ? 'Connecting...' : 'Get Tables'}
                </button>
              </div>
              {availableTables.length > 0 && (
                <select
                  name="table_name"
                  value={mariadbConfig.table_name}
                  onChange={handleMariadbConfigChange}
                  style={selectStyle}
                >
                  <option value="">Select a table</option>
                  {availableTables.map(table => (
                    <option key={table} value={table}>{table}</option>
                  ))}
                </select>
              )}
            </div>
          </div>
        );

      case 'dynamodb':
        return (
          <div style={containerStyle}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  placeholder="Region"
                  name="region"
                  value={dynamoConfig.region}
                  onChange={handleDynamoConfigChange}
                  style={inputStyle}
                />
                <input
                  type="text"
                  placeholder="Access Key ID"
                  name="access_key_id"
                  value={dynamoConfig.access_key_id}
                  onChange={handleDynamoConfigChange}
                  style={inputStyle}
                />
                <input
                  type="password"
                  placeholder="Secret Access Key"
                  name="secret_access_key"
                  value={dynamoConfig.secret_access_key}
                  onChange={handleDynamoConfigChange}
                  style={inputStyle}
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  placeholder="Table Name"
                  name="table_name"
                  value={dynamoConfig.table_name}
                  onChange={handleDynamoConfigChange}
                  style={inputStyle}
                />
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const renderApiConfig = () => {
    const inputStyle = {
      padding: '10px 14px',
      borderRadius: '10px',
      backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.7)',
      color: colors.text,
      border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.16)'}`,
      margin: '5px',
      width: '200px',
      fontSize: '13px',
      fontFamily: 'inherit',
      letterSpacing: '-0.005em',
      outline: 'none',
      transition: 'border-color 0.2s ease'
    };

    const buttonStyle = {
      padding: '10px 20px',
      borderRadius: '100px',
      background: fetchingForms
        ? (isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)')
        : (isDarkMode ? '#f0ede4' : '#1a1a1a'),
      color: fetchingForms
        ? (isDarkMode ? '#6b6862' : '#908c82')
        : (isDarkMode ? '#0d0d0e' : '#F8F5EE'),
      border: 'none',
      cursor: fetchingForms ? 'not-allowed' : 'pointer',
      fontSize: '13px',
      fontWeight: '500',
      letterSpacing: '-0.005em',
      margin: '5px',
      fontFamily: 'inherit',
      transition: 'all 0.2s ease',
      opacity: fetchingForms ? 0.75 : 1,
    };

    const containerStyle = {
      padding: '20px',
      borderRadius: '10px',
      border: `0.5px solid ${colors.border}`
    };

    switch (apiConnection) {
      case 'surveycto':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <p style={{ fontSize: '13px', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>
              Note that you need a paid SurveyCTO subscription with API access enabled.
            </p>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <input
                type="url"
                placeholder="Server URL (e.g., https://yourserver.surveycto.com)"
                name="server_url"
                value={surveyCTOConfig.server_url}
                onChange={handleSurveyCTOConfigChange}
                style={inputStyle}
                required
              />
              <input
                type="text"
                placeholder="Username (email)"
                name="username"
                value={surveyCTOConfig.username}
                onChange={handleSurveyCTOConfigChange}
                style={inputStyle}
                required
              />
            </div>
            
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <input
                type="password"
                placeholder="Password"
                name="password"
                value={surveyCTOConfig.password}
                onChange={handleSurveyCTOConfigChange}
                style={inputStyle}
                required
              />
              <input
                type="text"
                placeholder="Form ID (e.g., back_check_4)"
                name="form_id"
                value={surveyCTOConfig.form_id}
                onChange={handleSurveyCTOConfigChange}
                style={inputStyle}
                required
              />
            </div>
            
            <div style={{ 
              fontSize: '13px', 
              color: '#818cf8', 
              padding: '10px 12px',
              backgroundColor: isDarkMode ? 'rgba(129, 140, 248, 0.1)' : 'rgba(129, 140, 248, 0.08)',
              borderRadius: '10px',
              border: `0.5px solid ${isDarkMode ? 'rgba(129, 140, 248, 0.3)' : 'rgba(129, 140, 248, 0.2)'}`,
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px'
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, marginTop: '2px' }}>
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 6v6l4 2"/>
              </svg>
              <span>
                <strong>Rate Limit:</strong> SurveyCTO enforces a 5-minute wait between full data downloads. 
                If you see a "wait" error, please try again after the specified time.
              </span>
            </div>
            
            <div style={{ fontSize: '13px', color: isDarkMode ? '#808080' : '#666666', marginTop: '5px' }}>
              Form ID is the unique identifier for your SurveyCTO form. 
              Find it in your SurveyCTO server under the "Design" tab.
            </div>
          </div>
          
        );

      case 'kobo':
        return (
          <div style={containerStyle}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  placeholder="Server URL"
                  name="server_url"
                  value={koboConfig.server_url}
                  onChange={handleKoboConfigChange}
                  style={inputStyle}
                />
                <input
                  type="text"
                  placeholder="Username"
                  name="username"
                  value={koboConfig.username}
                  onChange={handleKoboConfigChange}
                  style={inputStyle}
                />
                <input
                  type="password"
                  placeholder="Password"
                  name="password"
                  value={koboConfig.password}
                  onChange={handleKoboConfigChange}
                  style={inputStyle}
                />
              </div>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  placeholder="Asset UID"
                  name="asset_uid"
                  value={koboConfig.asset_uid}
                  onChange={handleKoboConfigChange}
                  style={inputStyle}
                />
                <input
                  type="text"
                  placeholder="Export Setting ID (optional)"
                  name="export_setting_id"
                  value={koboConfig.export_setting_id}
                  onChange={handleKoboConfigChange}
                  style={inputStyle}
                />
                <input
                  type="text"
                  placeholder="Sheet Name (optional)"
                  name="sheet_name"
                  value={koboConfig.sheet_name}
                  onChange={handleKoboConfigChange}
                  style={inputStyle}
                />
              </div>
            </div>
          </div>
        );

      case 'odk':
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <p style={{ fontSize: '13px', color: isDarkMode ? '#9CA3AF' : '#6B7280' }}>
              Connect to your ODK Central server to load form submissions.
            </p>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <input
                type="url"
                placeholder="Server URL (e.g., https://your-server.odk.cloud)"
                name="server_url"
                value={odkConfig.server_url}
                onChange={handleOdkConfigChange}
                style={inputStyle}
                required
              />
              <input
                type="email"
                placeholder="Email"
                name="email"
                value={odkConfig.email}
                onChange={handleOdkConfigChange}
                style={inputStyle}
                required
              />
            </div>
            
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <input
                type="password"
                placeholder="Password"
                name="password"
                value={odkConfig.password}
                onChange={handleOdkConfigChange}
                style={inputStyle}
                required
              />
              <input
                type="number"
                placeholder="Project ID (numeric)"
                name="odk_project_id"
                value={odkConfig.odk_project_id}
                onChange={handleOdkConfigChange}
                style={inputStyle}
                required
              />
              <input
                type="text"
                placeholder="Form ID (xmlFormId)"
                name="form_id"
                value={odkConfig.form_id}
                onChange={handleOdkConfigChange}
                style={inputStyle}
                required
              />
            </div>
            
            <div style={{ fontSize: '13px', color: isDarkMode ? '#808080' : '#666666', marginTop: '5px' }}>
              Project ID is the numeric ID visible in your ODK Central URL (e.g., /projects/<strong>5</strong>/forms).
              Form ID is the xmlFormId shown in the form settings.
            </div>
          </div>
        );

      case 'surveysolutions':
        return (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '15px',
            padding: '24px',
            backgroundColor: isDarkMode ? 'rgba(255, 193, 7, 0.1)' : 'rgba(255, 193, 7, 0.1)',
            borderRadius: '10px',
            textAlign: 'center'
          }}>
            <h3 style={{ 
              color: '#FF8C00', 
              margin: '0 0 10px 0',
              fontSize: '18px',
              fontWeight: 500
            }}>
              Use PostgreSQL for Survey Solutions Data
            </h3>
            <p style={{ 
              color: isDarkMode ? '#9e9e9eff' : '#333333',
              fontSize: '15px',
              lineHeight: '1.6',
              margin: '0'
            }}>
              Survey Solutions' API uses an asynchronous export workflow (start job → wait for generation → download ZIP files), 
              which isn't ideal for real-time data access.
            </p>
            <p style={{ 
              color: isDarkMode ? '#9e9e9eff' : '#333333',
              fontSize: '15px',
              lineHeight: '1.6',
              margin: '10px 0 0 0'
            }}>
              For faster, direct access to your data, use the 
              <strong> PostgreSQL option under Database Connection</strong> to connect to your Survey Solutions database.
            </p>
            <p style={{ 
              color: isDarkMode ? '#CCCCCC' : '#666666',
              fontSize: '13px',
              fontStyle: 'italic',
              margin: '10px 0 0 0'
            }}>
              PostgreSQL provides immediate access to all interviews without export delays.
            </p>
          </div>
        );

      default:
        return null;
    }
  };
  
  
  
  return (
    
    <div className="selectDataContainer"
      
      style={{
        width: '100%',
        maxWidth: isDataPreviewVisible ? '100%' : '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto',
        marginRight: '5px',
        marginLeft: '5px',
        marginBottom: '0px',
        marginTop: isDataPreviewVisible ? '10px' : '10px',
        padding: '0px',
        borderBottom: isExpandedPS ? (isDarkMode ? '0px solid #5c5c5c' : '0px solid #dfdfdf') : '0px solid #dfdfdf',
        // backgroundColor: isDarkMode ? 'transparent' : '#f9f9f9ff',
        transition: 'margin-top 1s ease-in-out'   
      }}>
      
      <div
        onClick={toggleExpandPS}
        className="dl-configure-row"
      >
        <span className="dl-configure-row-label">
          {isDataPreviewVisible ? 'change data source' : 'configure data source'}
        </span>

        <div className={`dl-expand-chip ${isExpandedPS ? 'active' : ''}`}>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M6 9l6 6 6-6"/>
          </svg>
        </div>
      </div>
      
      <div style={{   // insider
        border: 'none',
        marginTop: '8px',
        marginBottom: '16px',
        justifyContent: 'center',
        overflowX: 'hidden',
        overflowY: 'hidden',
        transition: 'max-height 0.3s ease-in-out',
        width: '100%',
        maxHeight: isExpandedPS ? '2000px' : '0',
        display: isExpandedPS ? 'flex' : 'none',
      }}>
        

        <div style={{ width: '100%' }}>

        {/* Connection Status Indicator - Show for both API and Database connections */}
        {savedAPIConfig && (
          (dataSource === 'api' && apiConnection === savedAPIConfig.connection_type) ||
          (dataSource === 'database' && databaseConnection === savedAPIConfig.connection_type)
        ) && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            padding: '12px 20px',
            marginBottom: '16px',
            backgroundColor: isDarkMode ? 'rgba(37, 99, 235, 0.1)' : 'rgba(37, 99, 235, 0.08)',
            borderRadius: '10px',
            border: `0.5px solid ${isDarkMode ? 'rgba(37, 99, 235, 0.2)' : 'rgba(37, 99, 235, 0.15)'}`,
          }}>
            {/* Connection Icon */}
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '10px',
              backgroundColor: isDarkMode ? 'rgba(37, 99, 235, 0.15)' : 'rgba(37, 99, 235, 0.12)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              {savedAPIConfig.connection_type === 'kobo' && (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2">
                  <path d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9"/>
                </svg>
              )}
              {savedAPIConfig.connection_type === 'surveycto' && (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2"/>
                  <path d="M9 9h6v6H9z"/>
                </svg>
              )}
              {savedAPIConfig.connection_type === 'odk' && (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2">
                  <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                  <path d="M2 17l10 5 10-5"/>
                  <path d="M2 12l10 5 10-5"/>
                </svg>
              )}
              {/* Database icons */}
              {savedAPIConfig.connection_type === 'postgresql' && (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2">
                  <ellipse cx="12" cy="5" rx="9" ry="3"/>
                  <path d="M21 12c0 1.66-4.03 3-9 3s-9-1.34-9-3"/>
                  <path d="M3 5v14c0 1.66 4.03 3 9 3s9-1.34 9-3V5"/>
                </svg>
              )}
              {savedAPIConfig.connection_type === 'mysql' && (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2">
                  <ellipse cx="12" cy="5" rx="9" ry="3"/>
                  <path d="M21 12c0 1.66-4.03 3-9 3s-9-1.34-9-3"/>
                  <path d="M3 5v14c0 1.66 4.03 3 9 3s9-1.34 9-3V5"/>
                </svg>
              )}
              {savedAPIConfig.connection_type === 'mariadb' && (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2">
                  <ellipse cx="12" cy="5" rx="9" ry="3"/>
                  <path d="M21 12c0 1.66-4.03 3-9 3s-9-1.34-9-3"/>
                  <path d="M3 5v14c0 1.66 4.03 3 9 3s9-1.34 9-3V5"/>
                </svg>
              )}
              {savedAPIConfig.connection_type === 'mongodb' && (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2">
                  <path d="M12 2C8 2 5 5 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-4-3-7-7-7z"/>
                  <circle cx="12" cy="9" r="2.5"/>
                </svg>
              )}
              {savedAPIConfig.connection_type === 'dynamodb' && (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2">
                  <path d="M12 2L2 7v10l10 5 10-5V7l-10-5z"/>
                  <path d="M12 22V12"/>
                  <path d="M22 7l-10 5"/>
                  <path d="M2 7l10 5"/>
                </svg>
              )}
              {!['kobo', 'surveycto', 'odk', 'postgresql', 'mysql', 'mariadb', 'mongodb', 'dynamodb'].includes(savedAPIConfig.connection_type) && (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2">
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>
                </svg>
              )}
            </div>

            {/* Connection Info */}
            <div style={{ flex: 1 }}>
              <div style={{
                fontSize: '13px',
                fontWeight: 500,
                color: '#2563EB',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
                Connected to {getConnectionDisplayName(savedAPIConfig.connection_type)}
              </div>
              <div style={{
                fontSize: '12px',
                color: colors.textMuted,
                marginTop: '2px',
              }}>
                {savedAPIConfig.settings?.username && !savedAPIConfig.settings?.host && `${savedAPIConfig.settings.username} • `}
                {savedAPIConfig.settings?.user && savedAPIConfig.settings?.host && `${savedAPIConfig.settings.user}@${savedAPIConfig.settings.host} • `}
                {savedAPIConfig.settings?.username && savedAPIConfig.settings?.host && savedAPIConfig.connection_type === 'mongodb' && `${savedAPIConfig.settings.username}@${savedAPIConfig.settings.host} • `}
                {savedAPIConfig.settings?.database && savedAPIConfig.settings?.table_name && `${savedAPIConfig.settings.database}.${savedAPIConfig.settings.table_name} • `}
                {savedAPIConfig.settings?.database && savedAPIConfig.settings?.collection && `${savedAPIConfig.settings.database}.${savedAPIConfig.settings.collection} • `}
                {savedAPIConfig.settings?.region && savedAPIConfig.settings?.table_name && savedAPIConfig.connection_type === 'dynamodb' && `${savedAPIConfig.settings.region}/${savedAPIConfig.settings.table_name} • `}
                {lastRefreshed ? `Last synced ${formatTimeSince(lastRefreshed)}` : 'Ready to sync'}
              </div>
            </div>

            {/* Schedule Dropdown */}
            <select
              value={refreshInterval}
              onChange={(e) => handleScheduleChange(e.target.value)}
              disabled={isUpdatingSchedule}
              style={{
                padding: '8px 12px',
                borderRadius: '10px',
                border: `0.5px solid ${isDarkMode ? 'rgba(37, 99, 235, 0.3)' : 'rgba(37, 99, 235, 0.2)'}`,
                backgroundColor: isDarkMode ? 'rgba(37, 99, 235, 0.1)' : 'rgba(37, 99, 235, 0.05)',
                color: '#2563EB',
                fontSize: '12px',
                fontWeight: '500',
                cursor: isUpdatingSchedule ? 'not-allowed' : 'pointer',
                outline: 'none',
                minWidth: '100px',
              }}
            >
              <option value="manual">Manual</option>
              <option value="hourly">Hourly</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
            </select>

            {/* Quick Refresh Button */}
            <button
              onClick={handleRefreshData}
              disabled={isRefreshing}
              className="dl-file-btn"
              style={{
                opacity: isRefreshing ? 0.6 : 1,
                cursor: isRefreshing ? 'not-allowed' : 'pointer',
              }}
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                style={{ animation: isRefreshing ? 'spin 1s linear infinite' : 'none' }}
              >
                <path d="M21 12a9 9 0 00-9-9 9.75 9.75 0 00-6.74 2.74L3 8"/>
                <path d="M3 3v5h5"/>
                <path d="M3 12a9 9 0 009 9 9.75 9.75 0 006.74-2.74L21 16"/>
                <path d="M16 16h5v5"/>
              </svg>
              {isRefreshing ? 'Syncing…' : 'Sync now'}
            </button>

            {/* Disconnect Button */}
            <button
              onClick={handleDisconnect}
              title="Disconnect and remove saved credentials"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '9px 16px',
                borderRadius: '100px',
                border: `0.5px solid ${isDarkMode ? 'rgba(248, 113, 113, 0.3)' : 'rgba(220, 38, 38, 0.25)'}`,
                backgroundColor: 'transparent',
                color: isDarkMode ? '#f87171' : '#dc2626',
                fontSize: '12.5px',
                fontWeight: 500,
                letterSpacing: '-0.005em',
                cursor: 'pointer',
                fontFamily: 'inherit',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = isDarkMode ? 'rgba(248, 113, 113, 0.08)' : 'rgba(220, 38, 38, 0.06)';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18.36 6.64a9 9 0 11-12.73 0"/>
                <line x1="12" y1="2" x2="12" y2="12"/>
              </svg>
              Disconnect
            </button>
          </div>
        )}

        {/* Main data source selection */}
        
        {/* Import Method Tabs */}
        <div className="dl-method-tabs">
          <button 
            className={`dl-method-tab ${dataSource === 'file' ? 'active' : ''}`}
            onClick={() => setDataSource('file')}
          >
            <FileUploadIcon size={14} color="currentColor" />
            File Upload
          </button>
          <button 
            className={`dl-method-tab ${dataSource === 'api' ? 'active' : ''}`}
            onClick={() => setDataSource('api')}
          >
            <APIIcon size={14} color="currentColor" />
            API Connection
          </button>
          <button 
            className={`dl-method-tab ${dataSource === 'database' ? 'active' : ''}`}
            onClick={() => setDataSource('database')}
          >
            <DatabaseIcon size={14} color="currentColor" />
            Database
          </button>
        </div>
        
        {/* Platform sub-tabs for API */}
        {dataSource === 'api' && (
          <div className="dl-platform-tabs">
            {[
              { id: 'kobo', icon: KoBoIcon, label: 'KoBoToolbox' },
              { id: 'surveycto', icon: SurveyCTOIcon, label: 'SurveyCTO' },
              { id: 'odk', icon: ODKIcon, label: 'ODK Central' },
              { id: 'surveysolutions', icon: SurveySolutionsIcon, label: 'Survey Solutions' },
            ].map(({ id, icon: Icon, label }) => (
              <button
                key={id}
                className={`dl-platform-tab ${apiConnection === id ? 'active' : ''}`}
                onClick={() => setApiConnection(id)}
              >
                <Icon size={13} color="currentColor" />
                {label}
              </button>
            ))}
          </div>
        )}

        {/* Database type sub-tabs */}
        {dataSource === 'database' && (
          <div className="dl-platform-tabs">
            {[
              { id: 'postgresql', icon: PostgreSQLIcon, label: 'PostgreSQL' },
              { id: 'mysql', icon: MySQLIcon, label: 'MySQL' },
              { id: 'mariadb', icon: MariaDBIcon, label: 'MariaDB' },
              { id: 'mongodb', icon: MongoDBIcon, label: 'MongoDB' },
              { id: 'dynamodb', icon: DynamoDBIcon, label: 'DynamoDB' },
            ].filter(({ id }) => !isLocalMode() || (id !== 'mongodb' && id !== 'dynamodb'))
             .map(({ id, icon: Icon, label }) => (
              <button
                key={id}
                className={`dl-platform-tab ${databaseConnection === id ? 'active' : ''}`}
                onClick={() => setDatabaseConnection(id)}
              >
                <Icon size={13} color="currentColor" />
                {label}
              </button>
            ))}
          </div>
        )}

        {/* Configuration sections */}
        <div style={{ 
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          marginTop: '20px', 
          marginBottom: '20px',
          padding: '0 20px',
          transition: '0.5s ease-in-out'
        }}>
          {dataSource === 'file' && renderFileUploadConfig()}
          {dataSource === 'database' && renderDatabaseConfig()}
          {dataSource === 'api' && renderApiConfig()}
        </div>

        {/* Load Data Button */}
        <div style={{ 
         display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '20px',
          flexDirection: 'column',
          marginTop: '20px', 
          marginBottom: '24px',
        }}>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <button
              onClick={handleLoadData}
              disabled={isLoadDataDisabled()}
              className={isLoadDataDisabled() ? '' : 'dl-primary-cta'}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                padding: '12px 26px',
                borderRadius: '100px',
                border: 'none',
                background: isLoadDataDisabled()
                  ? (isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)')
                  : (isDarkMode ? '#f0ede4' : '#1a1a1a'),
                color: isLoadDataDisabled()
                  ? (isDarkMode ? '#6b6862' : '#908c82')
                  : (isDarkMode ? '#0D0D0E' : '#F8F5EE'),
                fontSize: '13px',
                fontWeight: 500,
                letterSpacing: '-0.005em',
                cursor: isLoadDataDisabled() ? 'not-allowed' : 'pointer',
                transition: 'transform 0.2s ease',
                opacity: isLoadDataDisabled() ? 0.75 : 1,
                fontFamily: 'inherit',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              {loading && (
                <svg className="spinner" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                  <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                </svg>
              )}
              <span>{loading ? 'Loading...' : 'Load Data'}</span>
            </button>
            
            {/* Refresh Button - shown when on API or Database tab with matching connection type */}
            {savedAPIConfig && (
              (dataSource === 'api' && apiConnection === savedAPIConfig.connection_type) ||
              (dataSource === 'database' && databaseConnection === savedAPIConfig.connection_type)
            ) && (
              <button
                onClick={handleRefreshData}
                disabled={isRefreshing}
                title={lastRefreshed ? `Last refreshed: ${new Date(lastRefreshed).toLocaleString()}` : 'Refresh data'}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  padding: '12px 22px',
                  borderRadius: '100px',
                  border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.16)'}`,
                  background: 'transparent',
                  color: colors.text,
                  fontSize: '13px',
                  fontWeight: 500,
                  letterSpacing: '-0.005em',
                  cursor: isRefreshing ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit',
                  transition: 'all 0.2s ease',
                  opacity: isRefreshing ? 0.6 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!isRefreshing) {
                    e.currentTarget.style.backgroundColor = isDarkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  style={{
                    animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
                  }}
                >
                  <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
                  <path d="M3 3v5h5"/>
                  <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"/>
                  <path d="M16 16h5v5"/>
                </svg>
                {isRefreshing ? 'Refreshing…' : 'Refresh'}
              </button>
            )}
          </div>
          
          {/* Last refreshed info - show when on API or Database tab with matching connection type */}
          {savedAPIConfig && lastRefreshed && (
            (dataSource === 'api' && apiConnection === savedAPIConfig.connection_type) ||
            (dataSource === 'database' && databaseConnection === savedAPIConfig.connection_type)
          ) && (
            <div style={{
              fontSize: '12px',
              color: colors.textMuted,
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <path d="M12 6v6l4 2"/>
              </svg>
              Last synced: {new Date(lastRefreshed).toLocaleString()}
            </div>
          )}

          {/* ── Media folder (optional) ──
              Project-wide setting consumed by Data Preview, Audio Audit, etc.
              Picker only renders in Tauri — web users see a hint instead.
              Hidden when no projectId (initial load before project context). */}
          {projectId && (
            <div style={{
              marginTop: '8px',
              width: '100%',
              maxWidth: '600px',
              padding: '14px 16px',
              borderRadius: '4px',
              border: `1px dashed ${colors.border}`,
              backgroundColor: 'transparent',
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                marginBottom: mediaDir || tauriApi ? '8px' : 0,
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" strokeWidth="2"
                     style={{ color: colors.textSecondary, flexShrink: 0 }}>
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                </svg>
                <span style={{
                  fontSize: '13px',
                  fontWeight: 500,
                  color: colors.text,
                  flex: 1,
                }}>
                  Media folder
                </span>
                <span style={{
                  fontSize: '11px',
                  color: colors.textMuted,
                  letterSpacing: '0.02em',
                  textTransform: 'uppercase',
                }}>
                  optional
                </span>
              </div>

              {mediaDir ? (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '12px',
                }}>
                  <code style={{
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: colors.textSecondary,
                    backgroundColor: colors.surfaceBg,
                    padding: '4px 8px',
                    borderRadius: '3px',
                  }} title={mediaDir}>
                    {mediaDir}
                  </code>
                  {tauriApi && (
                    <button
                      onClick={handlePickMediaFolder}
                      disabled={pickingFolder}
                      style={{
                        padding: '4px 10px',
                        fontSize: '12px',
                        background: 'transparent',
                        color: colors.primary,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '3px',
                        cursor: pickingFolder ? 'wait' : 'pointer',
                        flexShrink: 0,
                      }}
                    >
                      {pickingFolder ? 'Choosing…' : 'Change'}
                    </button>
                  )}
                  <button
                    onClick={handleClearMediaFolder}
                    title="Clear media folder"
                    style={{
                      padding: '4px 8px',
                      fontSize: '12px',
                      background: 'transparent',
                      color: colors.textMuted,
                      border: `1px solid ${colors.border}`,
                      borderRadius: '3px',
                      cursor: 'pointer',
                      flexShrink: 0,
                    }}
                  >
                    Clear
                  </button>
                </div>
              ) : tauriApi ? (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                }}>
                  <span style={{
                    flex: 1,
                    fontSize: '12px',
                    color: colors.textSecondary,
                    lineHeight: 1.5,
                  }}>
                    Tell DataQuad where your photos, audio, video, and PDFs live to enable inline previews.
                  </span>
                  <button
                    onClick={handlePickMediaFolder}
                    disabled={pickingFolder}
                    style={{
                      padding: '6px 14px',
                      fontSize: '12px',
                      fontWeight: 500,
                      background: colors.primary,
                      color: 'white',
                      border: 'none',
                      borderRadius: '3px',
                      cursor: pickingFolder ? 'wait' : 'pointer',
                      opacity: pickingFolder ? 0.6 : 1,
                      flexShrink: 0,
                    }}
                  >
                    {pickingFolder ? 'Choosing…' : 'Choose folder'}
                  </button>
                </div>
              ) : (
                <span style={{
                  fontSize: '12px',
                  color: colors.textMuted,
                  lineHeight: 1.5,
                }}>
                  Available in DataQuad Desktop — picks a local folder containing your media files.
                </span>
              )}
            </div>
          )}

          {error && (
            <div style={{
              color: isDarkMode ? '#f87171' : '#dc2626',
              padding: '12px 18px',
              borderRadius: '10px',
              backgroundColor: isDarkMode ? 'rgba(248, 113, 113, 0.08)' : 'rgba(220, 38, 38, 0.08)',
              border: `0.5px solid ${isDarkMode ? 'rgba(248, 113, 113, 0.3)' : 'rgba(220, 38, 38, 0.25)'}`,
              fontSize: '13px',
              textAlign: 'center',
              maxWidth: '600px',
              letterSpacing: '-0.005em'
            }}>
              {error}
            </div>
          )}
        </div>
        </div>
      </div>
    </div>
  );
};

export default LoadPrimaryData;